/**
 * The owner's home-page switcher. Only rendered for an admin account (the backend says so in
 * `can_edit`), so visitors never see it.
 *
 * Clicking a layout PREVIEWS it in this browser only. "Make this live for everyone" saves it on
 * the backend, and from then on every visitor's home page is that one.
 */
import React, { useState } from "react";
import { HOME_HEADLINES, HOME_LAYOUTS, HOME_SKINS, HomeHeadline, HomeLayout, HomeSkin, Preview } from "./siteSettings";
import { HEADLINE_NAMES, LAYOUT_NAMES, SKIN_NAMES, SWITCHER, describe, describeFull } from "./siteLayoutText";

interface Props {
  live: Preview;
  shown: Preview;
  onPreview: (p: Preview | null) => void;
  onMakeLive: (p: Preview) => Promise<void>;
}

const CSS = `
.ccsw{position:fixed;left:16px;bottom:16px;z-index:13000;font:14px/1.4 Outfit,-apple-system,system-ui,sans-serif;color:#f6eff1}
.ccsw *{box-sizing:border-box}
.ccsw button{font:inherit;cursor:pointer;border:0;background:none;color:inherit}
.ccsw .pill{display:flex;align-items:center;gap:8px;background:rgba(18,14,17,.94);border:1px solid rgba(255,255,255,.14);border-radius:999px;padding:9px 16px;box-shadow:0 10px 30px rgba(0,0,0,.35);font-weight:600}
.ccsw .pill .dot{width:8px;height:8px;border-radius:50%;background:#2fd17c}
.ccsw .pill .dot.pv{background:#ffb020}
.ccsw .box{width:330px;max-width:calc(100vw - 32px);background:rgba(18,14,17,.97);border:1px solid rgba(255,255,255,.14);border-radius:18px;padding:16px;box-shadow:0 20px 60px rgba(0,0,0,.5)}
.ccsw .hd{display:flex;align-items:center;justify-content:space-between;font-weight:700;font-size:16px;margin-bottom:10px}
.ccsw .st{font-size:13px;color:#b9adb3;margin:0 0 4px}
.ccsw .st b{color:#fff}
.ccsw .st .pv{color:#ffb020}
.ccsw ul{list-style:none;margin:12px 0 0;padding:0;display:grid;gap:6px}
.ccsw li>button{width:100%;text-align:left;display:flex;align-items:center;gap:10px;border-radius:12px;padding:9px 10px;border:1px solid rgba(255,255,255,.08)}
.ccsw li>button:hover{background:rgba(255,255,255,.06)}
.ccsw li>button.on{border-color:#e61d50;background:rgba(230,29,80,.14)}
.ccsw .r{width:16px;height:16px;border-radius:50%;border:2px solid #6c6066;flex:none}
.ccsw .on .r{border-color:#e61d50;background:radial-gradient(#e61d50 45%,transparent 50%)}
.ccsw .nm{font-weight:600}
.ccsw .nt{display:block;font-size:12px;color:#a99ca2}
.ccsw .live{margin-left:auto;font-size:11px;font-weight:700;color:#2fd17c;letter-spacing:.04em}
.ccsw .skins{display:flex;flex-wrap:wrap;gap:6px;margin:6px 0 2px 36px}
.ccsw .sub{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#a99ca2;margin:10px 0 0 36px}
.ccsw .skins button{border:1px solid rgba(255,255,255,.18);border-radius:999px;padding:4px 12px;font-size:13px}
.ccsw .skins button.on{background:#e61d50;border-color:#e61d50;color:#fff}
.ccsw .go{margin-top:14px;display:grid;gap:8px}
.ccsw .go .mk{background:#e61d50;color:#fff;border-radius:999px;padding:11px;font-weight:700}
.ccsw .go .mk:disabled{opacity:.45;cursor:default}
.ccsw .go .bk{color:#b9adb3;font-weight:600;padding:4px}
.ccsw .msg{margin-top:10px;font-size:13px;color:#2fd17c}
.ccsw .msg.bad{color:#ff6b8b}
`;

const same = (a: Preview, b: Preview) =>
  a.layout === b.layout && (a.layout !== "simple" || (a.skin === b.skin && a.headline === b.headline));

const SiteLayoutSwitcher: React.FC<Props> = ({ live, shown, onPreview, onMakeLive }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const previewing = !same(shown, live);

  const pick = (layout: HomeLayout, skin: HomeSkin = shown.skin, headline: HomeHeadline = shown.headline) => {
    setMsg(null);
    const next = { layout, skin, headline };
    onPreview(same(next, live) ? null : next);
  };

  const makeLive = async () => {
    const what = describeFull(shown.layout, shown.skin, shown.headline);
    if (!window.confirm(SWITCHER.confirm(what))) return;
    setBusy(true);
    setMsg(null);
    try {
      await onMakeLive(shown);
      setMsg({ text: SWITCHER.saved(what), ok: true });
    } catch (e) {
      setMsg({ text: (e as Error)?.message || SWITCHER.failed, ok: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ccsw">
      <style>{CSS}</style>
      {!open ? (
        <button type="button" className="pill" onClick={() => setOpen(true)}>
          <span className={`dot${previewing ? " pv" : ""}`} />
          {SWITCHER.pill}: {describe(shown.layout, shown.skin)}
        </button>
      ) : (
        <div className="box" role="dialog" aria-label={SWITCHER.title}>
          <div className="hd">
            <span>{SWITCHER.title}</span>
            <button type="button" onClick={() => setOpen(false)} aria-label={SWITCHER.close}>
              ✕
            </button>
          </div>
          <p className="st">
            {SWITCHER.everyone}: <b>{describeFull(live.layout, live.skin, live.headline)}</b>
          </p>
          {previewing && (
            <p className="st">
              {SWITCHER.you}: <b>{describeFull(shown.layout, shown.skin, shown.headline)}</b> <span className="pv">({SWITCHER.onlyYou})</span>
            </p>
          )}
          <ul>
            {HOME_LAYOUTS.map((l) => (
              <li key={l}>
                <button type="button" className={shown.layout === l ? "on" : ""} onClick={() => pick(l)}>
                  <span className="r" />
                  <span>
                    <span className="nm">{LAYOUT_NAMES[l].name}</span>
                    <span className="nt">{LAYOUT_NAMES[l].note}</span>
                  </span>
                  {live.layout === l && <span className="live">LIVE</span>}
                </button>
                {l === "simple" && shown.layout === "simple" && (
                  <>
                    <p className="sub">{SWITCHER.skin}</p>
                    <div className="skins">
                      {HOME_SKINS.map((s) => (
                        <button type="button" key={s} className={shown.skin === s ? "on" : ""} onClick={() => pick("simple", s)}>
                          {SKIN_NAMES[s]}
                        </button>
                      ))}
                    </div>
                    <p className="sub">{SWITCHER.headline}</p>
                    <div className="skins">
                      {HOME_HEADLINES.map((h) => (
                        <button type="button" key={h} className={shown.headline === h ? "on" : ""} onClick={() => pick("simple", shown.skin, h)}>
                          {HEADLINE_NAMES[h]}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="go">
            <button type="button" className="mk" disabled={!previewing || busy} onClick={makeLive}>
              {busy ? SWITCHER.saving : SWITCHER.makeLive}
            </button>
            {previewing && (
              <button type="button" className="bk" onClick={() => onPreview(null)}>
                {SWITCHER.backToLive}
              </button>
            )}
          </div>
          {msg && <p className={`msg${msg.ok ? "" : " bad"}`}>{msg.text}</p>}
        </div>
      )}
    </div>
  );
};

export default SiteLayoutSwitcher;
